<script setup>
/**
 * @fileoverview Sidebar for Custom Mosaics.
 *
 * - **Step 1**: Select a Pattern (one of the patterns in data/patterns.json)
 * - **Step 2**: Select Colors (pick an Original Ceramic glaze, paint tiles in
 *   the preview; parts list and share link)
 * - **Step 3**: Select Grout Color
 *
 * At most one step is open at a time. The open step lives in the store so the
 * preview can send the user to Step 2 when they try to paint without a color.
 *
 * @component
 */
import { computed } from 'vue';
import { useMosaicStore } from '../stores/mosaicStore';
import AccordionSection from './Shared/AccordionSection.vue';
import PatternPicker from './PatternPicker.vue';
import ColorStep from './ColorStep.vue';
import GroutPicker from './GroutPicker.vue';

const store = useMosaicStore();

const step1Subtitle = computed(() =>
    store.selectedPattern ? store.selectedPattern.name : `${store.patterns.length} patterns`,
);
const step2Subtitle = computed(() => `${Object.keys(store.glazeColors).length} colors`);

function setStepOpen(step, open) {
    if (open) store.setOpenStep(step);
    else if (store.openStep === step) store.setOpenStep(0);
}
</script>

<template>
    <aside class="h-full min-h-0 overflow-auto">
        <div class="min-h-0 flex flex-col">
            <AccordionSection
                title="Select a Pattern"
                :open="store.openStep === 1"
                :subtitle="step1Subtitle"
                :expand-on-open="false"
                @update:open="(v) => setStepOpen(1, v)"
            >
                <template #title> <strong>Step 1:</strong> Select a Pattern </template>
                <PatternPicker />
            </AccordionSection>

            <AccordionSection
                title="Select Colors"
                :open="store.openStep === 2"
                :subtitle="step2Subtitle"
                :expand-on-open="false"
                @update:open="(v) => setStepOpen(2, v)"
            >
                <template #title> <strong>Step 2:</strong> Select Colors </template>
                <ColorStep />
            </AccordionSection>

            <AccordionSection
                title="Select Grout Color"
                :open="store.openStep === 3"
                :subtitle="store.groutColor?.name"
                :expand-on-open="false"
                @update:open="(v) => setStepOpen(3, v)"
            >
                <template #title> <strong>Step 3:</strong> Select Grout Color </template>
                <div class="p-4">
                    <GroutPicker />
                </div>
            </AccordionSection>
        </div>
    </aside>
</template>
